import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Image,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import AddressSheet from '../components/cart/AddressSheet';
import CouponSheet from '../components/cart/CouponSheet';
import RecurringSheet, {
  describeFirstVisit,
  describeRecurrence,
  Recurrence,
  toOrderRecurrence,
} from '../components/cart/RecurringSheet';
import SlotSheet, { Slot } from '../components/cart/SlotSheet';
import Button from '../components/ui/Button';
import ConfirmSheet from '../components/ui/ConfirmSheet';
import EmptyState from '../components/ui/EmptyState';
import Icon, { IconName } from '../components/ui/Icon';
import Text from '../components/ui/Text';
import {
  BookingMode,
  cancellationPolicy,
  describeSlot,
  slotStartIso,
  tipOptions,
} from '../data/booking';
import {
  durationBand,
  linesFromQuote,
  newReference,
  orderIdFor,
  PaymentOrder,
  wholeRupees,
} from '../data/payment';
import useCheckoutQuote from '../hooks/useCheckoutQuote';
import { ensureServerAddress } from '../services/bookings';
import { CheckoutRequest, withServiceTimes } from '../services/checkout';
import {
  cardDuration,
  cardPrice,
  cardWasPrice,
  hourChoices,
  hoursFor,
  isHourly,
  minutesOf,
  priceOf,
} from '../data/hourly';
import {
  addressNameFor,
  formatAddress,
  NO_ADDRESS,
  SavedAddress,
} from '../data/places';
import {
  Category,
  maxPerBooking,
  Service,
  SubCategory,
  subCategoryOf,
} from '../data/services';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { suggestFrom } from '../services/catalogue';
import {
  colors,
  hairline,
  inputText,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../theme';

/** Whole rupees, grouped - `Intl` is not reliably present on Android JSC. */
const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * The one horizontal inset for the whole screen.
 *
 * Header, every band and the footer all use it, so nothing steps in or out as
 * the page scrolls. It is `screenPadding` itself - the gutter this page was
 * drawn to is now the app's, so the cart sits on the same margin as everything
 * you reach it from.
 */
const PAGE_INSET = screenPadding;

/**
 * How far the group's icon pushes its title in - glyph plus the row's gap.
 *
 * The service lines are indented by the same amount so they start under the
 * category name rather than under its icon, which is what makes them read as
 * belonging to it.
 */
const GROUP_INDENT = 18 + spacing.sm;

/** Width of a card in the suggestions rail. */
const SUGGEST_WIDTH = 124;

/** The three booking types, with what each one promises. */
const MODES: Array<{
  key: BookingMode;
  label: string;
  blurb: string;
  icon: IconName;
}> = [
  { key: 'schedule', label: 'Slot', blurb: 'Choose a time', icon: 'calendar' },
  {
    key: 'recurring',
    label: 'Recurring',
    blurb: 'Repeat service',
    icon: 'clock',
  },
];

type Props = {
  /** Every service in the catalogue, so keys can be resolved to items. */
  services: Service[];
  categoryFor: (service: Service) => Category;
  /** Service key → quantity. */
  cart: Record<string, number>;
  /**
   * Service key → hours, for the ones sold by the hour.
   *
   * The basket's other half. Every price on this screen is the product of the
   * two, so a line read without this is a line priced at one hour - see
   * `data/hourly.ts`.
   */
  cartHours: Record<string, number>;
  onChangeQuantity: (service: Service, delta: number) => void;
  /** Changes how long an hourly line runs. Never called for a fixed one. */
  onChangeHours: (service: Service, hours: number) => void;
  onBack: () => void;
  /**
   * Where "Browse services" goes from the empty basket.
   *
   * Separate from `onBack` on purpose. Back means "the screen I came from",
   * which for the cart is usually Home; browsing means the services tab. They
   * used to be the same call, so someone with an empty basket who tapped a
   * button offering services was returned to Home to find them.
   */
  onBrowse: () => void;
  /**
   * Gated by the caller - this is where an account is finally needed.
   *
   * Hands over the whole order rather than letting payment re-derive it. The
   * coupon, the tip and the fee live here, and a second place that adds them
   * up is a second place that can print a different total from the one the
   * customer just tapped.
   */
  onCheckout: (order: PaymentOrder) => void;
  /**
   * Razorpay is being opened for this basket - placed and waiting for the
   * sheet. The pay button says so and ignores taps; there is no screen in
   * between, the sheet opens over the cart.
   */
  openingPayment?: boolean;
  /** Where the work happens, from the location flow. */
  address: string;
  /** The book the picker offers. Owned by `App`, persisted on every save. */
  addresses: SavedAddress[];
  /*
   * `onOpenSub` was declared here for the "add more" link and is gone with it.
   *
   * It was never passed by `HomeScreen`, and the subcategory it wanted comes
   * from the bundled `subCategoryOf`, which a live service never matches - so
   * the prop had no caller and no argument. See the note on the link itself.
   */
  /** Opens a suggested service's own sheet. */
  onOpenService?: (service: Service) => void;
  /** Chosen from the address sheet, or set by the location flow. */
  onChangeAddress?: (next: string) => void;
  /** Leaves for the full location flow, for an address not on file. */
  onAddAddress?: () => void;
  /**
   * The same flow, opened on a GPS fix instead of on the map alone.
   *
   * Separate from `onAddAddress` because they start the location screen at
   * different stages - see `HomeScreen`, which owns that choice.
   */
  onUseCurrentLocation?: () => void;
  /**
   * Empties the basket outright - the header's cross, confirmed first since
   * there is no undo. Omitted where the caller has nothing to clear (a plain
   * `cart` prop with no owning state), the same way the other optional
   * callbacks here already are.
   */
  onClearCart?: () => void;
  /**
   * Re-reads the catalogue the basket is priced against.
   *
   * This page more than any other: the basket holds a price that was true
   * when the service was tapped, and ops can change one while somebody is
   * still deciding. Optional for the same reason the callbacks above are - a
   * caller with no catalogue to re-read renders a page that does not pull.
   */
  onRefresh?: () => Promise<unknown>;
};

/**
 * The cart, as a screen rather than a sheet.
 *
 * A sheet was the wrong container: reviewing a basket is a destination, not a
 * glance. It wants the full height for the list, a checkout bar that is
 * unambiguously the end of the flow, and a back affordance rather than a
 * dismiss gesture that reads as "cancel".
 *
 * ---------------------------------------------------------------------------
 * THE ORDER OF THE PAGE
 * ---------------------------------------------------------------------------
 * Where, then what, then when, then what it costs:
 *
 *   0. the address, in the header - it decides what can be booked at all
 *   1. one basket card, with every trade grouped inside it
 *   2. what else goes with it
 *   3. instant / slot / recurring
 *   4. the address again, in full, with the option to add another
 *   5. coupons, behind a sheet
 *   6. price breakdown, collapsible
 *   7. tip, collapsed until asked for
 *   8. the cancellation terms
 *
 * The money is last on purpose. Every line above it can change the total, so a
 * breakdown placed higher would be a number the page then contradicts.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS REAL AND WHAT IS NOT
 * ---------------------------------------------------------------------------
 * The grouping and the pickers are real; nothing is reserved, and slots come
 * from a fixed list rather than live availability.
 *
 * The MONEY is entirely the server's. This screen used to compute its own
 * discount, tax and total against a hardcoded fee and a coupon list nobody on
 * the backend had ever heard of - see the note that used to live on
 * `data/booking.ts`'s `coupons`. Every one of those numbers now comes from
 * `POST checkout/quote` (see `services/checkout.ts` and `useCheckoutQuote`),
 * asked again on every change to the basket, the typed code, the coin toggle
 * or the tip, and rendered here exactly as it comes back. Nothing on this
 * screen adds, subtracts or multiplies a price - the one exception is the
 * per-service lines in the basket card, which were never fiction: the app has
 * always known what is in the basket and what each service's own price is,
 * and `itemTotal` below is just their sum for display.
 */
function CartScreen({
  services,
  categoryFor,
  cart,
  cartHours,
  onChangeQuantity,
  onChangeHours,
  onBack,
  onBrowse,
  onCheckout,
  openingPayment = false,
  address,
  addresses,
  onOpenService,
  onChangeAddress,
  onAddAddress,
  onUseCurrentLocation,
  onClearCart,
  onRefresh,
}: Props) {
  const resp = useResponsive();

  /*
   * The prices on this page are the ones about to be charged, so a stale one
   * is the most expensive kind of stale in the app. Killing and reopening was
   * the only way to correct it.
   */
  /**
   * Bumped on every pull, and handed to `SlotSheet`.
   *
   * The times in that picker are the city's opening hours, which ops edits -
   * and `useAvailability` is keyed on the DAY, so a day it has already
   * answered is never asked about again. Right while somebody taps along the
   * strip; wrong once the hours have changed underneath them, and the only
   * way out was to kill the app.
   *
   * A counter rather than clearing the data: the sheet keeps showing what it
   * has until the new answer lands, so the grid does not blink back to the
   * compiled one mid-refresh.
   */
  const [pullCount, setPullCount] = useState(0);

  const pull = usePullToRefresh(
    useCallback(() => {
      /*
       * ONE counter for everything a pull re-reads, because a pull is one
       * request: "show me what is true now".
       *
       * Three separate reads sit behind it - the catalogue, the day's opening
       * hours, and the checkout quote - and the quote is the one that was
       * missing. The bill is almost entirely platform settings: the service
       * fee, how much of a bill coins may pay for, whether coins may touch the
       * fee. Ops changing any of those left an open cart quoting yesterday's
       * numbers with no way for the customer to ask again.
       */
      setPullCount(n => n + 1);
      return onRefresh ? onRefresh() : Promise.resolve();
    }, [onRefresh]),
  );

  /* `schedule`, not `instant` - Instant is no longer offered (see `MODES`),
     and every basket now names a time before it can be paid for. */
  const [mode, setMode] = useState<BookingMode>('schedule');
  const [slot, setSlot] = useState<Slot | null>(null);
  /*
   * A service's own time, by service key, when the customer gave it one.
   *
   * `slot` above is the basket's time - "same time for all" - and a service
   * with nothing here follows it. Only read while the basket holds more than
   * one service; a single service is booked exactly as it always was.
   */
  const [serviceSlots, setServiceSlots] = useState<Record<string, Slot>>({});
  /** The service the time picker is choosing for, or null for the basket. */
  const [slotFor, setSlotFor] = useState<string | null>(null);
  const [recurrence, setRecurrence] = useState<Recurrence | null>(null);
  const [slotOpen, setSlotOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);

  const [addressOpen, setAddressOpen] = useState(false);
  /*
   * What to do once the address sheet has actually gone.
   *
   * "Add new" leaves this sheet for the location flow - and, signed out, for
   * the sign-in sheet. Both used to open in the same tick this one closed,
   * which iOS cannot do: the sign-in sheet never appeared and the screen stopped
   * taking touches. Parked here and run from the sheet's `onClosed` instead.
   */
  const afterAddressSheet = useRef<(() => void) | null>(null);
  const leaveAddressSheetFor = useCallback((next: () => void) => {
    afterAddressSheet.current = next;
    setAddressOpen(false);
  }, []);
  const runAfterAddressSheet = useCallback(() => {
    const next = afterAddressSheet.current;
    afterAddressSheet.current = null;
    next?.();
  }, []);
  const [couponOpen, setCouponOpen] = useState(false);
  /**
   * The coupon field's current value - typed freehand, or picked off the
   * offers list in `CouponSheet`. A plain string, deliberately: whether it
   * actually prices anything is answered by `checkout/quote`'s `codeRefusal`
   * / `appliedCode`, not decided here. `null` is "nothing entered".
   */
  const [couponCode, setCouponCode] = useState<string | null>(null);
  /**
   * Whether the "Use Homingo Coins" switch is on. Off by default - the same
   * reasoning the old `useRedemption`-backed row carried: the balance is the
   * customer's, and spending it should be a decision they made rather than
   * something quietly applied to a small job.
   */
  const [useCoins, setUseCoins] = useState(false);
  /** The header's cross - confirming before it actually empties the basket. */
  const [clearing, setClearing] = useState(false);

  /** `null` is "no tip chosen yet"; `0` is an explicit no. */
  const [tip, setTip] = useState<number | null>(null);
  /**
   * The custom-amount field, and what is currently typed in it.
   *
   * Held as a STRING rather than as the number: an input bound to a number
   * cannot represent an empty box or a half-typed "1" on the way to "150", and
   * both of those are states somebody passes through on every use. `tip` is
   * the number, derived on each keystroke.
   */
  const [customTipOpen, setCustomTipOpen] = useState(false);
  const [customTip, setCustomTip] = useState('');
  /**
   * Scrolls the tip card clear of the keyboard.
   *
   * The custom-amount field sits low on the page, and the window does not
   * reliably lift a focused input above the keyboard on an edge-to-edge
   * Android build - the amount was being typed under it, blind. `tipCardY`
   * is the tip card's offset in the scroll content, measured where the card
   * lays out; focusing the field scrolls that card to the top of the
   * viewport, which no keyboard reaches.
   */
  const scrollRef = useRef<ScrollView>(null);
  const tipCardY = useRef(0);
  /* Open from the start, like the price breakdown - a folded tip row was a
     control most people never found, and the options are one short line. */
  const [tipOpen, setTipOpen] = useState(true);

  /** Both start open - a total nobody can see is a total nobody trusts. */
  const [priceOpen, setPriceOpen] = useState(true);
  const [policyOpen, setPolicyOpen] = useState(false);

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const lines = useMemo(
    () =>
      services
        .filter(service => (cart[service.key] ?? 0) > 0)
        .map(service => ({
          service,
          quantity: cart[service.key],
          /*
           * The hours this line runs for, or undefined on a fixed service.
           * Carried on the line rather than looked up again at each of the
           * dozen places below that need it - one lookup, one answer, and no
           * chance of the price row and the duration row disagreeing.
           */
          hours: hoursFor(service, cartHours),
          /*
           * Rate × hours × quantity.
           *
           * `priceOf` is the first two: on a fixed service it is just the
           * price, on an hourly one it is the rate multiplied by the length.
           * Multiplying `service.price` directly here - which is what this
           * line used to do - prices every hourly booking at a single hour
           * while the platform charges for however many were picked.
           */
          total: priceOf(service, cartHours) * cart[service.key],
          /* The service's per-booking cap, set per service by ops - the
             line's + stops here. See `maxPerBooking`. */
          cap: maxPerBooking(service),
        })),
    [cart, cartHours, services],
  );

  /*
   * Which of the three booking types the WHOLE basket can actually go
   * through as - one mode books every line at once, so a mode is only
   * offered when every service in the cart supports it.
   *
   * `undefined` on a service (the offline seed carries no flags) reads as
   * "no restriction known" rather than as a block - an offline session
   * should not be unable to book anything just because the flag never
   * arrived. A live service always carries all three; the backend refuses
   * `POST /bookings` outright for a mode it does not support (that refusal
   * is what a mismatch here used to surface as, at payment, as a plain
   * "Booking error" nobody could act on).
   */
  const modesAllowed = useMemo(() => {
    const supports = (
      service: Service,
      flag: 'supportsInstant' | 'supportsScheduled' | 'supportsRecurring',
    ) => service[flag] ?? true;

    return {
      /* Instant is not offered any more, so it is never allowed - leaving it
         `true` would let the fallback below silently select a mode that has
         no control in the picker and no way back out of. */
      instant: false,
      schedule: lines.every(line =>
        supports(line.service, 'supportsScheduled'),
      ),
      recurring: lines.every(line =>
        supports(line.service, 'supportsRecurring'),
      ),
    } satisfies Record<BookingMode, boolean>;
  }, [lines]);

  /* The picked mode falls out from under a cart change - a line that only
     supports Instant is added while Recurring was selected, say - and has
     to move to a mode the new basket can actually go through as. */
  useEffect(() => {
    if (modesAllowed[mode]) {
      return;
    }
    const next = (Object.keys(modesAllowed) as BookingMode[]).find(
      key => modesAllowed[key],
    );
    if (next) {
      setMode(next);
    }
  }, [modesAllowed, mode]);

  /*
   * Grouped by subcategory, not category.
   *
   * "Cleaning" over a bathroom clean and a kitchen clean says they are the
   * same thing bought twice. The subcategory is the shelf they actually came
   * from, and it is what the "add more" link can point back at.
   */
  const groups = useMemo(() => {
    const out: Array<{ sub: SubCategory | null; lines: typeof lines }> = [];

    lines.forEach(line => {
      const sub = subCategoryOf(line.service) ?? null;
      const existing = out.find(group => group.sub?.key === sub?.key);
      if (existing) {
        existing.lines.push(line);
      } else {
        out.push({ sub, lines: [line] });
      }
    });

    return out;
  }, [lines]);

  /*
   * Built from `services` - the live catalogue this screen is already handed
   * - and NOT from `similarServices` in `data/services.ts`, which reads three
   * bundled lists and no live one.
   *
   * That rail was showing cards for services the platform has never sold, and
   * every card on it opens a sheet with an "Add to cart" button. A seed key is
   * not a uuid, so anything added from there failed at checkout on an id the
   * server had never heard of. See `suggestFrom`.
   */
  const suggestions = useMemo(
    () =>
      suggestFrom(
        services,
        lines.map(line => line.service),
      ),
    [services, lines],
  );

  // ------------------------------------------------------------------ money
  /*
   * The sum of the lines above - real, because the app has always known what
   * is in the basket and what each service's own price is. This is the LAST
   * arithmetic this screen performs on a price; the discount, the fee, the
   * tax-equivalent, the coins and the total are all `checkout/quote`'s own
   * answer - see the block below `resolvedAddressId`, once an address is
   * known to quote against.
   */
  const itemTotal = lines.reduce((sum, line) => sum + line.total, 0);
  const isEmpty = lines.length === 0;

  /*
   * Nothing can be booked without somewhere to send someone.
   *
   * The checkout button changes rather than being disabled: a dead "Proceed to
   * Pay" says the app is broken, while "Add address" says what is missing and
   * fixes it in the same tap.
   */
  /**
   * The chosen address as a row in the book, not just the line on screen.
   *
   * Everything below asks this rather than the string, because the string
   * cannot answer the only question that matters at checkout: does this place
   * have a pin.
   */
  const selected = useMemo(
    () => addresses.find(saved => formatAddress(saved) === address),
    [address, addresses],
  );

  /**
   * Somewhere to send someone - and "somewhere" means a pin, not a line.
   *
   * This used to be `address !== NO_ADDRESS`, which a line of text passes. The
   * server resolves the serviceable city from the COORDINATES, so an address
   * without them can be displayed and can never be booked against; the failure
   * surfaced after checkout, on the booking screen, as "this address has no
   * location saved". Asking here means the cart refuses to move on instead.
   */
  const addressReady = address !== NO_ADDRESS && Boolean(selected?.coords);

  /** Chosen, but pinned nowhere - the case that needs different words. */
  const addressNeedsPin = address !== NO_ADDRESS && !addressReady;

  /*
   * Which saved address this is, if it is one of them.
   *
   * Undefined for an address the location flow just produced - that saves into
   * state, not into the address book - so the row falls back to "Service at"
   * with no name rather than claiming it is Home.
   */
  /* Named against the live book, not the seeds - an address saved this
     session is on file even though `data/places` has never heard of it. */
  const name =
    address !== NO_ADDRESS ? addressNameFor(address, addresses) : undefined;

  /*
   * The server address id to quote and book against, resolved once so both
   * ask about the exact same row.
   *
   * ---------------------------------------------------------------------------
   * WHY THIS IS NOT JUST `selected.place.id`
   * ---------------------------------------------------------------------------
   * `checkout/quote` needs a real server address id - it is how the platform
   * resolves the serviceable city and decides whether anyone can be sent, the
   * same reason `POST /bookings` always has. A saved address already has one
   * (`selected.place.id`), but a pin just picked on the map - `'detected'`, in
   * `toSavedAddress`'s terms - does not exist on the server yet. Without this,
   * a customer who used their current location would see no total at all
   * until they happened to save the address from some other screen first.
   *
   * `ensureServerAddress` is the same call `HomeScreen.onCreateBooking` already
   * makes at the moment of booking; it dedupes by pin, so asking here too does
   * not leave a second row behind the day this address is actually booked.
   */
  const [resolvedAddressId, setResolvedAddressId] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (!selected || !selected.coords) {
      setResolvedAddressId(undefined);
      return;
    }
    if (selected.place.id !== 'detected') {
      setResolvedAddressId(selected.place.id);
      return;
    }

    let cancelled = false;
    ensureServerAddress(selected)
      .then(id => {
        if (!cancelled) {
          setResolvedAddressId(id);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedAddressId(undefined);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  /*
   * Several services, each able to have its own time. Scheduled baskets only:
   * a recurring plan has one pattern for the lot.
   */
  const perServiceTimes = mode === 'schedule' && lines.length > 1;

  /* The one service left in a basket that was split: its own time becomes the
     basket's, so it is not lost when the second service is removed. */
  useEffect(() => {
    if (lines.length !== 1 || slot) return;
    const own = serviceSlots[lines[0].service.key];
    if (own) setSlot(own);
  }, [lines, slot, serviceSlots]);

  /** One entry per unit - see `PaymentOrder.items` for why not a list of ids. */
  const availabilityItems = useMemo(
    () =>
      lines.flatMap(line =>
        Array.from({ length: line.quantity }, () => ({
          serviceId: line.service.key,
          ...(line.hours === undefined ? {} : { hours: line.hours }),
        })),
      ),
    [lines],
  );

  /**
   * What is quoted and booked: the same units, each carrying its service's own
   * time when it has one. A unit without one follows the basket's
   * `slotStartAt`, so a basket nobody split sends exactly what it used to.
   */
  const items = useMemo(
    () =>
      withServiceTimes(
        availabilityItems,
        perServiceTimes
          ? Object.fromEntries(
              Object.entries(serviceSlots).map(([key, own]) => [
                key,
                slotStartIso(own.date, own.time),
              ]),
            )
          : {},
      ),
    [availabilityItems, perServiceTimes, serviceSlots],
  );

  /* Only a picked slot has an instant to send - hoisted out of `startCheckout`
     so the quote and the eventual booking read the exact same value. */
  const slotStartAt = useMemo(
    () => (slot ? slotStartIso(slot.date, slot.time) : undefined),
    [slot],
  );

  /** `"50.00"` - the decimal string `checkout/quote` and `checkout` both take. */
  const tipAmount = tip ? tip.toFixed(2) : undefined;

  /**
   * What to quote, or `null` when there is not yet enough to ask about.
   *
   * `null` rather than an empty basket's worth of zeros: a missing address is
   * a reason to show NO total, not a reason to show a total of ₹0 that reads
   * as a real answer.
   */
  const quoteInput = useMemo<CheckoutRequest | null>(() => {
    if (isEmpty || !resolvedAddressId || items.length === 0) {
      return null;
    }
    return {
      items,
      addressId: resolvedAddressId,
      ...(slotStartAt ? { slotStartAt } : {}),
      ...(couponCode ? { code: couponCode } : {}),
      ...(tipAmount ? { tipAmount } : {}),
    };
  }, [isEmpty, resolvedAddressId, items, slotStartAt, couponCode, tipAmount]);

  const base = useCheckoutQuote(quoteInput, pullCount);

  /*
   * A SECOND quote, only while coins are actually being asked for.
   *
   * ---------------------------------------------------------------------------
   * WHY TWO QUOTES, NOT ONE WITH `coinsToRedeem` ALWAYS ATTACHED
   * ---------------------------------------------------------------------------
   * `maxRedeemableCoins` - the ceiling the "Use Homingo Coins" row offers - has
   * to come from a quote that is not itself shaped by how many coins were just
   * asked for, or turning the switch on would move the number the switch is
   * offering. `base` is that stable quote. Once the customer switches coins
   * on, this asks again with `coinsToRedeem` set to the ceiling `base` just
   * quoted, and ITS answer - with the coins' own line and the reduced total -
   * is what gets shown. Turning the switch back off falls to `base` at once,
   * with nothing further to wait for.
   */
  const withCoinsInput = useMemo<CheckoutRequest | null>(() => {
    if (
      !useCoins ||
      !quoteInput ||
      !base.quote ||
      base.quote.maxRedeemableCoins <= 0
    ) {
      return null;
    }
    return { ...quoteInput, coinsToRedeem: base.quote.maxRedeemableCoins };
  }, [useCoins, quoteInput, base.quote]);

  const withCoins = useCheckoutQuote(withCoinsInput, pullCount);

  const usingCoinsQuote = useCoins && withCoinsInput !== null;
  const quote = usingCoinsQuote ? withCoins.quote : base.quote;
  const quoteLoading = usingCoinsQuote ? withCoins.loading : base.loading;
  /** Whether `quote` is a proven price for the basket as it stands right
      now - false the instant anything changes, and through a failed
      re-quote, even though `quote` itself keeps showing the last good
      number. Gates payment; must never gate the display below. */
  const quoteCurrent = usingCoinsQuote ? withCoins.current : base.current;
  /** The server's sentence when the basket would pass a service's monthly
      cap - see `refusal` on `useCheckoutQuote`. */
  const quantityRefusal = base.refusal ?? withCoins.refusal;
  /*
   * The service whose own time the server refused, if that is what it
   * refused - `items[1].slotStartAt` names a unit, and the unit its service.
   * Said again on that service's time row, where it gets fixed.
   */
  const refusedField = base.refusal
    ? base.refusalField
    : withCoins.refusalField;
  const refusedIndex = /^items\[(\d+)\]\.slotStartAt$/.exec(
    refusedField ?? '',
  )?.[1];
  const refusedService =
    refusedIndex === undefined
      ? null
      : items[Number(refusedIndex)]?.serviceId ?? null;

  /** Most coins this basket could redeem - the ceiling the toggle offers,
      read off the COIN-FREE quote so switching coins on never moves it. */
  const maxRedeemableCoins = base.quote?.maxRedeemableCoins ?? 0;

  /** What the customer pays. The server's own figure - never a sum this
      screen performed on the lines below it. */
  const total = quote ? wholeRupees(quote.totalAmount) : 0;
  /** The itemized bill, read straight off the quote - see `linesFromQuote`. */
  const priceLines = quote ? linesFromQuote(quote.lines) : [];
  const coinsRedeemed = quote?.coinsRedeemed ?? 0;
  const codeRefusal = quote?.codeRefusal ?? null;
  const appliedCode = quote?.appliedCode ?? null;

  /** Opens the time picker for one service, or for the whole basket. */
  const openSlotPicker = (key: string | null) => {
    setSlotFor(key);
    setSlotOpen(true);
  };

  /** A service's time: its own, else the basket's. */
  const timeFor = (key: string): Slot | null =>
    (perServiceTimes ? serviceSlots[key] : undefined) ?? slot;
  const splitTimes =
    perServiceTimes &&
    lines.some(line => serviceSlots[line.service.key] !== undefined);

  const modeSummary =
    mode === 'schedule'
      ? splitTimes
        ? 'Each service has its own time'
        : slot
        ? describeSlot(slot.date, slot.time, today)
        : perServiceTimes
        ? 'Choose one time for all services'
        : 'Choose your preferred date & time'
      : recurrence
      ? describeRecurrence(recurrence)
      : 'Set a recurring schedule that works for you';

  /**
   * Whether the chosen mode is actually bookable yet.
   *
   * Both remaining modes open a sheet on a second tap - see the "Tap to
   * change" row below - and until that sheet is confirmed, `slot`/`recurrence`
   * stay `null` and `modeSummary` is only a placeholder ("Choose your
   * preferred date & time"). Without this check, `startCheckout` had nothing
   * stopping that placeholder from being minted straight into the booking as
   * its `when`.
   *
   * Instant used to satisfy this by merely being selected, which is why it is
   * gone from the list rather than just hidden: it was the one path to
   * checkout that named no time at all.
   */
  const modeReady =
    (mode === 'schedule' &&
      lines.length > 0 &&
      lines.every(line => timeFor(line.service.key) !== null)) ||
    (mode === 'recurring' && recurrence !== null);

  /**
   * The basket, restated as the thing being paid for.
   *
   * Built at the moment of checkout rather than kept in state: every value it
   * reads - the coupon, the tip, the slot - can still change up to the tap,
   * and an order object rebuilt on each render would be a new object every
   * keystroke for a screen that is not open yet.
   *
   * The reference is minted here, so a customer who backs out of payment and
   * comes back gets a new one. Two attempts at the same basket are two
   * bookings as far as the slot is concerned, and reusing the id would make
   * the first one's expiry look like the second one's.
   */
  /*
   * The terms, ticked per booking.
   *
   * Unticked every time on purpose. A box that arrives already ticked is not
   * consent, and this one carries the sentence a refund argument turns on -
   * "no refund if you refuse the professional at the door". A remembered
   * tick would be the platform agreeing with itself.
   *
   * `termsNudged` is the one-shot highlight for somebody who pressed the
   * button without reading: the footer button says what is missing rather
   * than going dead, and this is what it points at.
   */
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsNudged, setTermsNudged] = useState(false);

  const startCheckout = () => {
    // Guarded again here, not just by what the footer button offers to call:
    // this is the one place that actually mints the order, and a stray call
    // must not be able to send a `null` slot or recurrence past it. `!quote`
    // is guarded too - without a priced basket there is nothing real to hand
    // `onCheckout`, and the footer button is not offered until one exists.
    // `!quoteCurrent` is guarded separately from `!quote`: the screen may
    // still be SHOWING a quote (the last good one, kept up during a failed
    // re-quote) that is no longer proven good for the basket as it stands -
    // that quote must never be the one checkout runs against.
    if (!modeReady || !quote || !quoteCurrent) {
      return;
    }

    /*
     * `when` and `cadence` are not the same string `modeSummary` shows.
     *
     * The picker's row wants the PATTERN ("Every Thu, Afternoon…") whichever
     * mode is chosen, because that is what confirms what you just picked. The
     * booking wants something different for a recurring one: `when` is the
     * next visit's actual date, because that is the fact "when is this
     * happening" asks for, and the pattern goes in `cadence` instead - see the
     * note on `PaymentOrder`.
     */
    /* The first visit's time: the basket's, else the first service's own. */
    const firstVisit = slot ?? timeFor(lines[0].service.key)!;
    const when =
      mode === 'schedule'
        ? splitTimes
          ? `${describeSlot(firstVisit.date, firstVisit.time, today)} + more`
          : describeSlot(firstVisit.date, firstVisit.time, today)
        : describeFirstVisit(recurrence!, today);
    const cadence =
      mode === 'recurring' ? describeRecurrence(recurrence!) : undefined;
    /* The same choice in the server's terms - see `OrderRecurrence`. Built
       here, where the picker's answer actually lives, rather than parsed back
       out of `cadence` later. */
    const serverRecurrence =
      mode === 'recurring' ? toOrderRecurrence(recurrence!, today) : undefined;

    const reference = newReference();

    const trades = Array.from(
      new Set(lines.map(line => categoryFor(line.service).label)),
    ).join(', ');

    const minutes = lines.reduce(
      // Same correction as the price: an hourly service's own `minutes` is
      // one hour, and the visit is as long as the customer bought.
      (sum, line) => sum + minutesOf(line.service, cartHours) * line.quantity,
      0,
    );

    // `items` and `slotStartAt` are not rebuilt here - they are the exact
    // values already sent to `checkout/quote` above, hoisted to component
    // scope for that reason. Reconstructing them at this point risks the
    // booking asking for something subtly different from what was just
    // priced (an hourly line's length changing between the two, say).

    onCheckout({
      reference,
      orderId: orderIdFor(reference),
      // The first line is the heading. It is the one the basket was started
      // from, which is what someone recognises the booking by - and its trade
      // is the one the resulting booking files under.
      title: lines[0].service.name,
      category: lines[0].service.category,
      services: lines.map(line =>
        line.quantity > 1
          ? `${line.service.name} × ${line.quantity}`
          : line.service.name,
      ),
      items,
      trades,
      when,
      mode,
      ...(cadence ? { cadence } : {}),
      ...(serverRecurrence ? { recurrence: serverRecurrence } : {}),
      ...(slotStartAt ? { slotStartAt } : {}),
      duration: durationBand(minutes),
      address,
      /* The exact bill the customer read, off the exact quote that priced it
         - see `linesFromQuote`. Not reconstructed here. */
      lines: priceLines,
      total,
      /* Carried through so `POST checkout` is asked to re-apply the same
         code - the server re-validates it rather than trusting that a quote
         taken moments ago still holds. */
      ...(couponCode ? { code: couponCode } : {}),
      ...(tipAmount ? { tipAmount } : {}),
      /*
       * The coin count this quote actually redeemed.
       *
       * A count and not the rupee figure: the server prices coins itself, and
       * the two would drift the moment the rate changed between the quote and
       * the booking. Omitted when none were redeemed, so an order that
       * redeemed nothing carries no redemption at all.
       */
      ...(coinsRedeemed > 0 ? { coinsToRedeem: coinsRedeemed } : {}),
      /* What the customer ticked below the total. The server stamps the
         clock onto every booking this order places. */
      ...(termsAccepted ? { acceptedTerms: true } : {}),
    });
  };

  if (isEmpty) {
    return (
      <View style={styles.root}>
        <Header
          onBack={onBack}
          address={address}
          onPressAddress={() =>
            address !== NO_ADDRESS ? setAddressOpen(true) : onAddAddress?.()
          }
          // The same responsive inset the filled cart's header uses. Without
          // it this header sat at a different gutter from every other screen
          // on a wide device.
          inset={resp.sideInset}
        />
        {/* The shared empty state. Nothing has failed here - an empty basket
            is a basket you have not filled yet, which is why it carries no
            danger ink and no retry. */}
        <EmptyState
          icon="cart"
          title="Your cart is empty"
          message="Add a service and it will show up here."
          actionLabel="Browse services"
          onAction={onBrowse}
        />
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <Header
        inset={resp.sideInset}
        onBack={onBack}
        address={address}
        onPressAddress={() =>
          address !== NO_ADDRESS ? setAddressOpen(true) : onAddAddress?.()
        }
        onClear={onClearCart ? () => setClearing(true) : undefined}
      />

      <ScrollView
        ref={scrollRef}
        {...(onRefresh
          ? {
              refreshControl: (
                <RefreshControl
                  refreshing={pull.refreshing}
                  onRefresh={pull.onRefresh}
                  tintColor={colors.primary}
                  colors={[colors.primary]}
                />
              ),
            }
          : null)}
        /* Zero on a phone; on a tablet it narrows the basket to a readable
           column and centres it. See `theme/responsive.ts`. */
        contentContainerStyle={[styles.page, resp.column]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* ------------------------------------------- 1. one basket card */}
        <View style={styles.card}>
          {/* The offset separates one trade from the next, so the first group
              does not get one - there is nothing above it to be separated
              from, and unconditionally it left a band of empty section under
              the header. */}
          {groups.map((group, i) => (
            <React.Fragment key={group.sub?.key ?? 'other'}>
              {/* Half width, so it reads as a break between trades rather than
                  as the card being cut in two. The first group has nothing
                  above it to be separated from. */}
              {i > 0 ? <View style={styles.groupRule} /> : null}

              <View style={i > 0 ? styles.group : null}>
                <View style={styles.groupHead}>
                  <Icon
                    name={categoryFor(group.lines[0].service).icon}
                    size={18}
                    color={colors.primary}
                  />
                  <Text
                    variant="title"
                    color={colors.ink}
                    style={styles.groupTitle}
                  >
                    {group.sub?.label ?? 'Services'}
                  </Text>
                  <View style={styles.pill}>
                    <Text variant="caption" color={colors.primary}>
                      {group.lines.reduce(
                        (sum, line) => sum + line.quantity,
                        0,
                      )}{' '}
                      {group.lines.length === 1 ? 'item' : 'items'}
                    </Text>
                  </View>
                </View>

                {group.lines.map(
                  ({ service, quantity, hours, total: lineTotal, cap }) => (
                    <View key={service.key} style={styles.line}>
                      <View style={styles.lineBody}>
                        <Text variant="subtitle" numberOfLines={2}>
                          {service.name}
                        </Text>
                        <Text
                          variant="caption"
                          color={colors.inkMuted}
                          numberOfLines={1}
                        >
                          {/*
                        The length actually bought, and on an hourly line the
                        rate behind it.

                        `service.minutes` alone reads "60 mins" on a
                        three-hour booking, because on an hourly service that
                        field is one hour rather than the visit. Saying
                        "3 hrs · ₹499/hr" here is also what accounts for the
                        line total on the right - without it a customer sees
                        ₹1,497 against a service their basket says costs ₹499.
                      */}
                          {hours === undefined
                            ? `${service.minutes} mins`
                            : `${hours} ${
                                hours === 1 ? 'hr' : 'hrs'
                              } · ${rupees(service.price)}/hr`}
                        </Text>

                        {/*
                      Changing the length without going back to the service.

                      The basket is where a booking is actually reviewed, and
                      "three hours, not two" is the commonest correction there
                      is - sending someone back to the service page to make it
                      would lose the rest of what they had set up here.

                      A row of chips rather than the stepper on the right: that
                      stepper means quantity, and two identical controls a
                      centimetre apart meaning different things is how somebody
                      books eight hours by accident.
                    */}
                        {isHourly(service) ? (
                          <View style={styles.hourRow}>
                            {hourChoices(service).map(choice => {
                              const picked = choice === hours;
                              return (
                                <Pressable
                                  key={choice}
                                  accessibilityRole="radio"
                                  accessibilityState={{ selected: picked }}
                                  accessibilityLabel={`${
                                    service.name
                                  }, ${choice} ${
                                    choice === 1 ? 'hour' : 'hours'
                                  }`}
                                  onPress={() => onChangeHours(service, choice)}
                                  hitSlop={6}
                                  style={[
                                    styles.hourChip,
                                    picked ? styles.hourChipPicked : null,
                                  ]}
                                >
                                  <Text
                                    variant="caption"
                                    color={
                                      picked ? colors.onPrimary : colors.inkBody
                                    }
                                  >
                                    {choice}h
                                  </Text>
                                </Pressable>
                              );
                            })}
                          </View>
                        ) : null}

                        {/*
                          This service's own time, when the basket holds more
                          than one. A service with none follows the time chosen
                          for all of them; tapping gives it its own, from the
                          times free for THIS service. Each one becomes its own
                          booking at its own time, all paid together.
                        */}
                        {perServiceTimes ? (
                          <View style={styles.lineTime}>
                            {/* The same button as the footer's, smaller:
                                choosing this service's time is an action,
                                not a note under its name. */}
                            <Button
                              label={
                                serviceSlots[service.key]
                                  ? describeSlot(
                                      serviceSlots[service.key].date,
                                      serviceSlots[service.key].time,
                                      today,
                                    )
                                  : slot
                                  ? describeSlot(slot.date, slot.time, today)
                                  : 'Choose a time'
                              }
                              size="sm"
                              fullWidth={false}
                              leadingIcon="clock"
                              accessibilityLabel={`Time for ${service.name}`}
                              onPress={() =>
                                !addressReady
                                  ? address !== NO_ADDRESS
                                    ? setAddressOpen(true)
                                    : onAddAddress?.()
                                  : openSlotPicker(service.key)
                              }
                            />
                            {refusedService === service.key ? (
                              <Text variant="caption" color={colors.danger}>
                                {quantityRefusal}
                              </Text>
                            ) : null}
                          </View>
                        ) : null}
                      </View>

                      <View style={styles.lineRight}>
                        {/*
                          An hourly line has no quantity, so it has no stepper.

                          Length is the only thing there is to change, and the
                          chips on the left are where it is changed. A stepper
                          beside them offered a second, different answer to the
                          same question - press it and the basket held two
                          one-hour visits rather than one two-hour one, which
                          is two professionals in the same house on the same
                          day and not what anybody meant.

                          What is left is the one thing still worth doing from
                          here: taking the line out.
                        */}
                        {isHourly(service) ? (
                          <Pressable
                            accessibilityRole="button"
                            accessibilityLabel={`Remove ${service.name}`}
                            onPress={() => onChangeQuantity(service, -quantity)}
                            hitSlop={8}
                            style={styles.step}
                          >
                            <Icon
                              name="trash"
                              size={15}
                              color={colors.inkMuted}
                              strokeWidth={2}
                            />
                          </Pressable>
                        ) : (
                        <View style={styles.stepper}>
                          <Pressable
                            accessibilityRole="button"
                            accessibilityLabel={
                              quantity === 1
                                ? `Remove ${service.name}`
                                : `One fewer ${service.name}`
                            }
                            onPress={() => onChangeQuantity(service, -1)}
                            hitSlop={8}
                            style={styles.step}
                          >
                            <Icon
                              name="minus"
                              size={13}
                              color={colors.primary}
                              strokeWidth={2.4}
                            />
                          </Pressable>
                          <Text variant="labelSm" color={colors.primary}>
                            {quantity}
                          </Text>
                          <Pressable
                            accessibilityRole="button"
                            accessibilityLabel={
                              quantity >= cap
                                ? `Maximum ${cap} ${service.name} per booking`
                                : `One more ${service.name}`
                            }
                            accessibilityState={{
                              disabled: quantity >= cap,
                            }}
                            disabled={quantity >= cap}
                            onPress={() => onChangeQuantity(service, 1)}
                            hitSlop={8}
                            style={styles.step}
                          >
                            <Icon
                              name="plus"
                              size={13}
                              color={
                                quantity >= cap
                                  ? colors.inkDisabled
                                  : colors.primary
                              }
                              strokeWidth={2.4}
                            />
                          </Pressable>
                        </View>
                        )}

                        <Text variant="labelSm" align="center">
                          {rupees(lineTotal)}
                        </Text>
                        {/* Why the + went grey. The checkout refuses more
                            than this in one booking; ops set it per service.
                            Never on an hourly line - it has no stepper to
                            explain, and its own limit is the hour chips. */}
                        {!isHourly(service) && quantity >= cap ? (
                          <Text
                            variant="caption"
                            color={colors.inkMuted}
                            align="center"
                          >
                            {`Max ${cap} per booking`}
                          </Text>
                        ) : null}
                      </View>
                    </View>
                  ),
                )}
              </View>
            </React.Fragment>
          ))}

          {/*
            A way back to browsing. Small and unadorned: it is a way out, not a
            ninth thing on the page to weigh.

            -------------------------------------------------------------------
            IT DID NOTHING AT ALL, AND WAS DEAD TWICE OVER
            -------------------------------------------------------------------
            It used to open the SHELF the first basket line came from -
            `onOpenSub(groups[0].sub)` - which is a nicer offer ("more of
            this") and reached nothing:

             - `sub` comes from `subCategoryOf`, which matches against the
               bundled subcategories in `data/services.ts`. A live service
               carries a server slug, so it is always `undefined` and the `if`
               never passed;
             - `onOpenSub` is optional and `HomeScreen` does not pass it, so
               even a real `sub` would have called nothing.

            Two independent reasons for a button to be inert, neither of which
            reports anything. It was a control that looked like a control.

            `onBrowse` is the prop that already exists for exactly this, and is
            what the empty basket's own "Browse services" uses - so a full
            basket and an empty one now agree about where "more" is.
          */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Add more items"
            onPress={onBrowse}
            style={({ pressed }) => [
              styles.more,
              pressed ? styles.pressed : null,
            ]}
          >
            <Icon
              name="plus"
              size={14}
              color={colors.primary}
              strokeWidth={2.4}
            />
            <Text variant="labelSm" color={colors.primary}>
              Add more items
            </Text>
          </Pressable>
        </View>

        {/* Under the lines it is about, verbatim from the server - the
            steppers above are where it gets fixed. */}
        {quantityRefusal ? (
          <View style={[styles.card, styles.rowCard]}>
            <Icon name="alert" size={16} color={colors.danger} />
            <Text
              variant="caption"
              color={colors.danger}
              style={styles.rowCardText}
            >
              {quantityRefusal}
            </Text>
          </View>
        ) : null}

        {/* ------------------------------------------- 2. what goes with it */}
        {suggestions.length ? (
          <View style={styles.card}>
            <View style={styles.cardHead}>
              <Icon name="grid" size={16} color={colors.primary} />
              <Text variant="subtitle" style={styles.cardHeadText}>
                More services you may need
              </Text>
            </View>

            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.rail}
            >
              {suggestions.map(service => (
                <Pressable
                  key={service.key}
                  accessibilityRole="button"
                  accessibilityLabel={`${service.name}, ${rupees(
                    service.price,
                  )}`}
                  onPress={() => onOpenService?.(service)}
                  style={({ pressed }) => [
                    styles.suggest,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <View style={styles.suggestArt}>
                    {service.image ? (
                      <Image
                        source={service.image}
                        style={styles.photo}
                        resizeMode="cover"
                      />
                    ) : (
                      <Icon
                        name={categoryFor(service).icon}
                        size={24}
                        color={colors.primary}
                      />
                    )}

                    {/* Over the picture's lower-right, the way a menu card
                        carries its add - the control belongs to the picture,
                        and below it would read as the start of the caption. */}
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Add ${service.name} to cart`}
                      /*
                        The press stops here.

                        This button sits INSIDE the card's own `Pressable`, so
                        without the stop a single tap ran both handlers: the
                        service was added AND its details sheet opened over the
                        cart, where the same service could be added a second
                        time. One tap, two adds, and a sheet nobody asked for.

                        `stopPropagation` on the native event is what prevents
                        the bubble; the parent is a sibling in the tree, not a
                        wrapper we can opt out of by re-ordering.
                      */
                      onPress={event => {
                        event.stopPropagation();
                        onChangeQuantity(service, 1);
                      }}
                      hitSlop={8}
                      style={styles.suggestAdd}
                    >
                      <Icon
                        name="plus"
                        size={14}
                        color={colors.onPrimary}
                        strokeWidth={2.6}
                      />
                    </Pressable>
                  </View>

                  <Text
                    variant="caption"
                    numberOfLines={2}
                    style={styles.suggestName}
                  >
                    {service.name}
                  </Text>

                  {/*
                    The same three facts a card carries on Home, in the same
                    order - struck figure, price, duration. See `ServiceCard`.

                    This used to print `rupees(service.price)` and nothing
                    else, which is wrong twice over on the services that have
                    a struck price or are sold by the hour: it dropped the
                    saving entirely, and it showed an hourly service's rate as
                    though it were the price of the job. `cardPrice` and
                    `cardWasPrice` are the two functions that already know the
                    difference, and using them is what keeps this rail
                    agreeing with the card the customer saw on Home.
                  */}
                  <View style={styles.suggestPrices}>
                    {cardWasPrice(service, rupees) ? (
                      <Text
                        variant="caption"
                        color={colors.inkMuted}
                        style={styles.suggestWas}
                      >
                        {cardWasPrice(service, rupees)}
                      </Text>
                    ) : null}
                    <Text variant="labelSm" color={colors.primary}>
                      {cardPrice(service, rupees)}
                    </Text>
                  </View>

                  <View style={styles.suggestDuration}>
                    <Icon name="clock" size={11} color={colors.inkMuted} />
                    <Text variant="caption" color={colors.inkMuted}>
                      {cardDuration(service)}
                    </Text>
                  </View>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        ) : null}

        {/* ---------------------------------------------------- 3. the when */}
        <View style={styles.card}>
          <Text variant="subtitle" style={styles.cardTitle}>
            Booking Type
          </Text>

          <View style={styles.modes}>
            {MODES.map(option => {
              const active = option.key === mode;
              /* Not filtered out of the list entirely - a mode the basket
                 cannot use right now still has to be SEEN as unavailable, or
                 "why is Recurring just missing" is a harder question than
                 "why is Recurring greyed out". */
              const allowed = modesAllowed[option.key];
              return (
                <Pressable
                  key={option.key}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active, disabled: !allowed }}
                  accessibilityLabel={
                    allowed
                      ? `${option.label}. ${option.blurb}`
                      : `${option.label}. Coming soon.`
                  }
                  disabled={!allowed}
                  onPress={() => setMode(option.key)}
                  style={[
                    styles.mode,
                    active ? styles.modeOn : null,
                    allowed ? null : styles.modeOff,
                  ]}
                >
                  <Icon
                    name={option.icon}
                    size={18}
                    color={
                      !allowed
                        ? colors.inkDisabled
                        : active
                        ? colors.primary
                        : colors.inkMuted
                    }
                  />
                  <Text
                    variant="labelSm"
                    /* `inkSubtle` is the same green as `inkMuted`, so an
                       unavailable mode used to read exactly as live as the
                       one next to it. */
                    color={
                      !allowed
                        ? colors.inkDisabled
                        : active
                        ? colors.primary
                        : colors.ink
                    }
                  >
                    {option.label}
                  </Text>

                  {/*
                    Why the tile is dead, on the tile itself.

                    A greyed control with no caption reads as broken. The
                    note under the picker says the same thing once, but it is
                    a general sentence about "some services" - it does not
                    say WHICH mode, and on a two-tile picker the customer
                    still has to work that out. This names it where they are
                    already looking.

                    "Coming soon" rather than "unavailable" because that is
                    what it is: the mode is switched off per service on the
                    backend (`supportsRecurring`), not refused because of
                    anything the customer did or could change by editing
                    their basket.
                  */}
                  {!allowed ? (
                    <Text variant="caption" color={colors.inkDisabled}>
                      Coming soon
                    </Text>
                  ) : null}
                </Pressable>
              );
            })}
          </View>

          {/* Gone on purpose: each tile now carries its own "Coming soon", so
              this general line ("Some services in your cart only support
              certain booking types") said the same thing less precisely and
              one place further from where the customer was looking. */}

          {/* Unconditional now. Both remaining modes need a choice made, and
              this row is where it is made - Instant was the only one that
              named no time, and it is no longer offered. */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={modeSummary}
            onPress={() =>
              /* The address first, as the pay button already does: the times
                 on offer depend on where the visit is. */
              !addressReady
                ? address !== NO_ADDRESS
                  ? setAddressOpen(true)
                  : onAddAddress?.()
                : mode === 'schedule'
                ? openSlotPicker(null)
                : setRecurringOpen(true)
            }
            style={styles.modeNote}
          >
            <Icon name="calendar" size={17} color={colors.primary} />
            <View style={styles.modeNoteText}>
              <Text variant="labelSm" color={colors.primary}>
                {modeSummary}
              </Text>
              <Text variant="caption" color={colors.inkMuted}>
                {perServiceTimes
                  ? 'Tap for the same time for all, or set one per service above'
                  : 'Tap to change'}
              </Text>
            </View>
            <Icon name="chevronRight" size={16} color={colors.borderStrong} />
          </Pressable>
        </View>

        {/* --------------------------------------------------- 4. the where */}
        <View style={styles.card}>
          {/*
            One row, one tap.

            It used to carry a heading, the line, a label pill and a "Change"
            link, plus a separate "Add a new address" button - five things for
            one fact. All of them opened the same sheet, and the sheet already
            offers adding. What is left says where the work happens and which
            address that is, and the chevron says it can be changed.
          */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={
              address !== NO_ADDRESS
                ? `Service at ${name ?? 'this address'}, ${address}. Change`
                : 'Add an address'
            }
            onPress={() => setAddressOpen(true)}
            style={({ pressed }) => [
              styles.addressRow,
              pressed ? styles.pressed : null,
            ]}
          >
            <View style={styles.addressMark}>
              <Icon name="pin" size={17} color={colors.primary} />
            </View>

            <View style={styles.addressText}>
              <Text variant="labelSm">
                {address !== NO_ADDRESS
                  ? `Service at${name ? ` · ${name}` : ''}`
                  : 'Service at'}
              </Text>
              <Text variant="caption" color={colors.inkMuted} numberOfLines={2}>
                {addressNeedsPin
                  ? `${address} - no location set`
                  : address !== NO_ADDRESS
                  ? address
                  : 'Add an address to book'}
              </Text>
              {/*
                The flat/landmark line, read back at the one moment it matters.

                It was saved and sent to the server all along, but nothing on
                the CHECKOUT path ever showed it - so the field looked like a
                demo that saved nowhere. Shown under the address it belongs
                to, exactly as the address book's own rows already show it.
              */}
              {selected?.note ? (
                <Text
                  variant="caption"
                  color={colors.inkMuted}
                  numberOfLines={1}
                >
                  {selected.note}
                </Text>
              ) : null}
            </View>

            <Icon name="chevronRight" size={17} color={colors.borderStrong} />
          </Pressable>
        </View>

        {/* -------------------------------------------------- 5. the coupon */}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            appliedCode ? `${appliedCode} applied` : 'Offers and coupons'
          }
          onPress={() => setCouponOpen(true)}
          style={({ pressed }) => [
            styles.card,
            styles.rowCard,
            pressed ? styles.pressed : null,
          ]}
        >
          <Icon name="gift" size={17} color={colors.primary} />
          <Text variant="subtitle" style={styles.rowCardText}>
            Offers & Coupons
          </Text>
          {appliedCode ? (
            <Text variant="caption" color={colors.primary}>
              {appliedCode} applied
            </Text>
          ) : null}
          <Icon name="chevronRight" size={16} color={colors.borderStrong} />
        </Pressable>

        {/*
          The server's own reason a typed code did not price, verbatim.

          Only ever shown when a `code` was actually sent and the quote came
          back with a `codeRefusal` for it - never a sentence this screen
          worked out from comparing the basket to a minimum itself. A listed
          offer is a candidate, not a guarantee; this is the guarantee.
        */}
        {codeRefusal ? (
          <View style={[styles.card, styles.rowCard]}>
            <Icon name="alert" size={16} color={colors.danger} />
            <Text
              variant="caption"
              color={colors.danger}
              style={styles.rowCardText}
            >
              {codeRefusal.message}
            </Text>
          </View>
        ) : null}

        {/* --------------------------------------------------- 6. the coins */}
        {/*
          Only when the COIN-FREE quote says something can actually be spent.

          A row reading "0 coins" is furniture: it cannot be switched on, and
          it takes a line of the cart to say so. `maxRedeemableCoins` is read
          off `base`, not off whichever quote is currently displayed, so the
          row's own presence never flickers as the switch is toggled.
        */}
        {maxRedeemableCoins > 0 ? (
          <Pressable
            accessibilityRole="switch"
            accessibilityState={{ checked: useCoins }}
            accessibilityLabel={`Use Homingo Coins, up to ${maxRedeemableCoins} coins available on this booking`}
            onPress={() => setUseCoins(on => !on)}
            style={({ pressed }) => [
              styles.card,
              styles.rowCard,
              pressed ? styles.pressed : null,
            ]}
          >
            <Icon name="wallet" size={17} color={colors.primary} />

            <View style={styles.rowCardText}>
              <Text variant="subtitle">Use Homingo Coins</Text>
              {/*
                What this booking will actually take, from the quote that is
                CURRENTLY on screen - not a figure this row works out on its
                own from the ceiling above.
              */}
              <Text variant="caption" color={colors.inkMuted}>
                {useCoins && coinsRedeemed > 0 && quote
                  ? `${coinsRedeemed} coins applied - ${rupees(
                      wholeRupees(quote.walletDiscountAmount),
                    )} off`
                  : `Up to ${maxRedeemableCoins} coins usable here`}
              </Text>
            </View>

            {/*
              A drawn switch rather than a `Switch`.

              React Native's own switch has a platform look this cart does not
              otherwise use, and it cannot be pressed by tapping the row -
              which is the target most people aim at. This is the whole row.
            */}
            <View style={[styles.toggle, useCoins ? styles.toggleOn : null]}>
              <View style={[styles.knob, useCoins ? styles.knobOn : null]} />
            </View>
          </Pressable>
        ) : null}

        {/* ------------------------------------------------- 7. the numbers */}
        <View style={styles.card}>
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: priceOpen }}
            accessibilityLabel="Price details"
            onPress={() => setPriceOpen(open => !open)}
            style={styles.disclosure}
          >
            <Icon name="creditCard" size={17} color={colors.primary} />
            <Text variant="subtitle" style={styles.rowCardText}>
              Price Details
            </Text>
            {priceOpen ? null : (
              <Text variant="labelSm">{quote ? rupees(total) : '-'}</Text>
            )}
            <Icon
              name={priceOpen ? 'chevronDown' : 'chevronRight'}
              size={16}
              color={colors.borderStrong}
            />
          </Pressable>

          {priceOpen ? (
            <View style={styles.prices}>
              {quote ? (
                <>
                  {/*
                    The bill, exactly as `checkout/quote` sent it - Items, Fee,
                    Subscription, Coupon, Coins, Tip, whichever of those apply,
                    in the order it gave them. Nothing here recombines them;
                    `linesFromQuote` only reads the sign off each one.
                  */}
                  {priceLines.map(line => (
                    <PriceRow
                      key={line.label}
                      label={line.label}
                      value={
                        line.credit
                          ? `- ${rupees(line.amount)}`
                          : rupees(line.amount)
                      }
                      tone={line.credit ? 'credit' : 'plain'}
                    />
                  ))}

                  <View style={styles.totalRow}>
                    <Text variant="subtitle">
                      {coinsRedeemed > 0 ? 'To pay' : 'Total'}
                    </Text>
                    <Text variant="titleLg">{rupees(total)}</Text>
                  </View>

                  {Number(quote.discountAmount) > 0 ? (
                    <View style={styles.saved}>
                      <Icon
                        name="shieldCheck"
                        size={15}
                        color={colors.primary}
                      />
                      <Text variant="caption" color={colors.primary}>
                        You saved {rupees(wholeRupees(quote.discountAmount))} on
                        this booking
                      </Text>
                    </View>
                  ) : null}

                  {/*
                    What the voucher did NOT use, said before it is asked for.

                    A ₹2,000 voucher on a ₹1,200 bill takes ₹1,200 and leaves
                    ₹800. Said here because the alternative is a customer
                    watching most of a voucher disappear into one booking and
                    assuming it is gone - the coins arrive days later, on a
                    screen they were not looking at.

                    "Once this service is done" is the whole condition, and it
                    is stated rather than implied: the coins are locked until
                    then and the cart must not read as though they are
                    spendable today.
                  */}
                  {(quote.voucherRemainderCoins ?? 0) > 0 ? (
                    <View style={styles.saved}>
                      <Icon name="wallet" size={15} color={colors.primary} />
                      <Text variant="caption" color={colors.primary}>
                        {quote.voucherRemainderCoins} coins left over from your
                        voucher - yours to spend once this service is done
                      </Text>
                    </View>
                  ) : null}
                </>
              ) : (
                <Text variant="bodySm" color={colors.inkMuted}>
                  {quoteLoading
                    ? 'Working out your total…'
                    : quantityRefusal ??
                      'Choose a serviceable address to see your total.'}
                </Text>
              )}
            </View>
          ) : null}
        </View>

        {/* ----------------------------------------------------- 7. the tip */}
        <View
          style={styles.card}
          /* The card is a direct child of the scroll content, so this y is
             content-relative - exactly what `scrollTo` wants. */
          onLayout={event => {
            tipCardY.current = event.nativeEvent.layout.y;
          }}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: tipOpen }}
            accessibilityLabel="Tip your professional"
            onPress={() => setTipOpen(open => !open)}
            style={styles.disclosure}
          >
            <Icon name="heart" size={17} color={colors.primary} />
            <Text variant="subtitle" style={styles.rowCardText}>
              Tip your professional
            </Text>
            <Text variant="labelSm" color={colors.primary}>
              {tip ? rupees(tip) : tipOpen ? '' : 'Add tip'}
            </Text>
            <Icon
              name={tipOpen ? 'chevronDown' : 'chevronRight'}
              size={16}
              color={colors.borderStrong}
            />
          </Pressable>

          {tipOpen ? (
            <View style={styles.tips}>
              {tipOptions.map(amount => {
                const active = tip === amount;
                return (
                  <Pressable
                    key={amount}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    accessibilityLabel={`Tip ${rupees(amount)}`}
                    onPress={() => {
                      /* A preset and a typed amount are the same field. Picking
                         one must close the other, or the bill shows ₹50 while
                         the box beside it still reads 75. */
                      setCustomTipOpen(false);
                      setCustomTip('');
                      setTip(active ? null : amount);
                    }}
                    style={[styles.tip, active ? styles.tipOn : null]}
                  >
                    <Text
                      variant="labelSm"
                      align="center"
                      color={active ? colors.onPrimary : colors.ink}
                    >
                      {rupees(amount)}
                    </Text>
                  </Pressable>
                );
              })}

              {/*
                A typed amount, where "No tip" used to sit.

                ---------------------------------------------------------------
                WHY THE "NO TIP" CHIP IS GONE AND NOTHING REPLACED IT
                ---------------------------------------------------------------
                Not tipping is already the default - `tip` starts null and the
                line never appears on the bill. The chip existed to let someone
                say so explicitly, which meant the one way to leave nothing was
                to press a button declaring it, in front of a row of amounts.
                Tapping a chosen amount again still clears it, so the way out
                is where the way in was.

                Custom is what people actually reached for: the three presets
                are round numbers, and a customer who wants ₹75 had no way to
                say it.
              */}
              <Pressable
                accessibilityRole="button"
                accessibilityState={{ selected: customTipOpen }}
                accessibilityLabel="Enter a custom tip amount"
                onPress={() => {
                  /* Closing the field clears what was typed into it - leaving
                     a tip applied by a control that is no longer on screen is
                     how somebody pays for something they cannot see. */
                  if (customTipOpen) {
                    setCustomTip('');
                    setTip(null);
                  }
                  setCustomTipOpen(open => !open);
                }}
                style={[styles.tip, customTipOpen ? styles.tipOn : null]}
              >
                <Text
                  variant="labelSm"
                  align="center"
                  color={customTipOpen ? colors.onPrimary : colors.ink}
                >
                  Custom
                </Text>
              </Pressable>
            </View>
          ) : null}

          {tipOpen && customTipOpen ? (
            <View style={styles.customTip}>
              <Text variant="labelSm" color={colors.inkMuted}>
                ₹
              </Text>
              <TextInput
                value={customTip}
                onChangeText={next => {
                  /*
                   * Digits only, and capped.
                   *
                   * `keyboardType` is a hint, not a guarantee - the numeric pad
                   * on Android still offers a comma and a minus, and a paste
                   * can carry anything. Stripping here means `Number()` below
                   * cannot produce `NaN` and land it in the order total.
                   *
                   * The cap is a fat-finger guard: an extra zero on a ₹200 tip
                   * is ₹2,000 added to a bill somebody is about to approve.
                   */
                  const digits = next.replace(/[^0-9]/g, '').slice(0, 4);
                  setCustomTip(digits);
                  setTip(digits ? Number(digits) : null);
                }}
                keyboardType="number-pad"
                placeholder="Enter amount"
                placeholderTextColor={colors.inkSubtle}
                style={styles.customTipInput}
                accessibilityLabel="Custom tip amount in rupees"
                maxLength={4}
                autoFocus
                /*
                 * Above the keyboard, always. Delayed past the keyboard's own
                 * animation so the scroll measures the resized window rather
                 * than racing it.
                 */
                onFocus={() => {
                  setTimeout(() => {
                    scrollRef.current?.scrollTo({
                      y: Math.max(0, tipCardY.current - spacing.md),
                      animated: true,
                    });
                  }, 250);
                }}
              />
            </View>
          ) : null}
        </View>

        {/* -------------------------------------------------- 8. the terms */}
        <View style={styles.card}>
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: policyOpen }}
            accessibilityLabel={cancellationPolicy().title}
            onPress={() => setPolicyOpen(open => !open)}
            style={styles.disclosure}
          >
            <Icon name="shieldCheck" size={17} color={colors.primary} />
            <View style={styles.rowCardText}>
              <Text variant="subtitle">{cancellationPolicy().title}</Text>
              <Text variant="caption" color={colors.inkMuted}>
                Know before you book
              </Text>
            </View>
            <Icon
              name={policyOpen ? 'chevronDown' : 'chevronRight'}
              size={16}
              color={colors.borderStrong}
            />
          </Pressable>

          {policyOpen ? (
            <Text
              variant="bodySm"
              color={colors.inkBody}
              style={styles.policyBody}
            >
              {cancellationPolicy().detail}
            </Text>
          ) : null}
        </View>
      </ScrollView>

      {/* ------------------------------------------------------- checkout */}
      <View
        style={[
          styles.footer,
          {
            /*
              A gap under the row, not the safe-area inset.

              This used to be `max(insets.bottom, spacing.md)`, which reserved
              the gesture area - but `BottomNav` is rendered as a SIBLING
              directly below this screen and already clears that itself. So
              the inset was a second allowance for the same strip of screen,
              and it left the button sitting hard against the tab bar with the
              two reading as one welded block.

              A plain gap is all this needs: it separates the button from the
              nav, and the nav's own padding handles the gesture bar.
            */
            paddingBottom: spacing.md,
            /* The bar is pinned outside the scroller, so it does not inherit
               the page's column and has to centre its own contents on it. */
            paddingHorizontal: PAGE_INSET + resp.sideInset,
          },
        ]}
      >
        {/*
          The terms, read before the money and not after it.

          Above the total rather than buried in the scroller: it is the last
          thing between a customer and a charge, and the sentence that matters
          is the second one - a professional refused at the door is not a
          refund. A link to a page nobody opens would say the same thing and
          be read by nobody.
        */}
        <Pressable
          accessibilityRole="checkbox"
          accessibilityState={{ checked: termsAccepted }}
          onPress={() => {
            setTermsAccepted(!termsAccepted);
            setTermsNudged(false);
          }}
          style={[styles.termsRow, termsNudged && styles.termsRowNudged]}
        >
          <View
            style={[styles.termsBox, termsAccepted && styles.termsBoxOn]}
          >
            {termsAccepted && (
              <Icon name="check" size={13} color={colors.onPrimary} />
            )}
          </View>
          <Text variant="caption" color={colors.inkMuted} style={styles.termsText}>
            <Text variant="caption" color={colors.ink}>
              All Homingo professionals are trained and verified. Any
              available professional may be assigned to this booking, and a
              preferred one cannot be requested.
            </Text>
            {'  '}
            If you refuse the professional at your door, no refund will be
            issued.
          </Text>
        </Pressable>

        <View style={styles.footerRow}>
          <View style={styles.footerTotal}>
            {/* What leaves the card, not what the booking cost - the coins'
                share is already netted into the server's own `totalAmount`. */}
            <Text variant="titleLg">{quote ? rupees(total) : '-'}</Text>
            <Text variant="caption" color={colors.inkMuted}>
              {coinsRedeemed > 0 ? 'To pay' : 'Total'}
            </Text>
          </View>
          <View style={styles.footerAction}>
            {/*
              What is missing, not a disabled button.
              -------------------------------------------------------------
              Same reasoning as `addressReady` above, extended to the booking
              type and now to the quote itself: a customer who chose
              "Recurring" and never opened the sheet needs to be told what is
              left, not shown a button that looks broken - and neither does
              one still waiting on a price. `Proceed to Pay` only appears once
              an address, a time and a real quote all exist.
            */}
            <Button
              loading={openingPayment}
              label={
                /* Placing the basket and minting its order, a second or
                   two, before Razorpay's own sheet covers the cart. */
                openingPayment
                  ? 'Opening Razorpay…'
                  : addressNeedsPin
                  ? 'Set address location'
                  : !addressReady
                  ? 'Add address'
                  : !modeReady
                  ? mode === 'schedule'
                    ? perServiceTimes
                      ? 'Choose one time for all services'
                      : 'Choose a time'
                    : 'Set your schedule'
                  : quantityRefusal && !quoteCurrent
                  ? 'Reduce quantity to continue'
                  : !quote
                  ? 'Working out your total…'
                  : /*
                      `quote` is still the last good price - shown above,
                      below - but it is no longer PROVEN good for the basket
                      as it now stands, so this is not offered as "Proceed to
                      Pay" until a fresh quote confirms it.
                    */
                  !quoteCurrent
                  ? 'Updating your total…'
                  : /*
                      Nothing left to charge.

                      When the wallet covers the whole bill there is no
                      payment to proceed to, and "Proceed to Pay" would open a
                      screen asking for ₹0. The step still happens - the
                      payment screen is also where the booking is confirmed -
                      but the button says what it actually does.
                    */
                  total === 0
                  ? 'Confirm Booking'
                  : /*
                      Last of the gates, and deliberately last: there is no
                      point asking somebody to agree to terms about a booking
                      they have not finished describing.
                    */
                  !termsAccepted
                  ? 'Accept the terms to continue'
                  : /* A plan is reviewed first; everything else is paid
                       before it is booked - never cash on the spot. */
                  mode === 'recurring'
                  ? 'Review Booking'
                  : 'Proceed to Pay'
              }
              onPress={
                openingPayment
                  ? () => {}
                  : !addressReady
                  ? () => setAddressOpen(true)
                  : !modeReady
                  ? () =>
                      mode === 'schedule'
                        ? openSlotPicker(null)
                        : setRecurringOpen(true)
                  : !quote || !quoteCurrent
                  ? () => {}
                  : /* Not a dead button: it points at the box instead. */
                  !termsAccepted
                  ? () => setTermsNudged(true)
                  : startCheckout
              }
            />
          </View>
        </View>

        <View style={styles.footerNote}>
          <Icon name="lock" size={13} color={colors.inkMuted} />
          <Text variant="caption" color={colors.inkMuted}>
            Secure payments. Easy cancellations.
          </Text>
        </View>
      </View>

      <SlotSheet
        visible={slotOpen}
        /* A service's own time when choosing for one, else the basket's. */
        slot={slotFor ? serviceSlots[slotFor] ?? slot : slot}
        /* Re-reads the day's availability after a pull, even one this sheet
           has already asked about. See `pullCount`. */
        reloadKey={pullCount}
        /*
         * The server's row id for the chosen address - `resolvedAddressId`,
         * the same one the quote uses.
         *
         * This used to pass `selected.place.id` and drop a `'detected'` pin
         * (picked on the map, not yet filed) as undefined. But the cart files
         * that pin itself, just above, for the quote - so the id existed and
         * was thrown away here, and the sheet fell back to the compiled
         * 7:00 AM–8:00 PM grid with none of the city's hours, closed days or
         * full times applied.
         */
        addressId={resolvedAddressId}
        /* A new booking: no address, no grid - see `needsAddress`. */
        needsAddress
        onChooseAddress={() => {
          setSlotOpen(false);
          if (address !== NO_ADDRESS) setAddressOpen(true);
          else onAddAddress?.();
        }}
        /* The basket as it will be booked, so a time is closed when there are
           not enough professionals for all of it - not just for one line.
           Choosing for one service asks about that service alone. */
        items={
          slotFor
            ? availabilityItems.filter(item => item.serviceId === slotFor)
            : availabilityItems
        }
        /* The other way out of a basket too long for the end of the day.
           `onBrowse` is the prop that already takes somebody to the
           catalogue - the same one 'Add more items' uses. */
        onChooseShorter={() => {
          setSlotOpen(false);
          onBrowse();
        }}
        onClose={() => setSlotOpen(false)}
        onConfirm={next => {
          if (slotFor) {
            setServiceSlots(current => ({ ...current, [slotFor]: next }));
          } else {
            /* "Same time for all": one visit again, as a basket always was. */
            setSlot(next);
            setServiceSlots({});
          }
          setSlotOpen(false);
        }}
      />

      <RecurringSheet
        visible={recurringOpen}
        recurrence={recurrence}
        onClose={() => setRecurringOpen(false)}
        onConfirm={next => {
          setRecurrence(next);
          setRecurringOpen(false);
        }}
      />

      <AddressSheet
        addresses={addresses}
        visible={addressOpen}
        current={address}
        onClose={() => setAddressOpen(false)}
        onSelect={next => {
          onChangeAddress?.(formatAddress(next));
          setAddressOpen(false);
        }}
        // Close before leaving: the sheet is a Modal, and one left mounted
        // over the location flow keeps its scrim on top.
        onAddNew={() => leaveAddressSheetFor(() => onAddAddress?.())}
        onClosed={runAfterAddressSheet}
        /* Same close-first rule as adding. The difference is where the
           location flow starts - see `onUseCurrentLocation` on the props. */
        onUseCurrentLocation={() =>
          leaveAddressSheetFor(() => onUseCurrentLocation?.())
        }
        // The same destination as adding, for now. The location flow cannot
        // load an existing address, so editing one opens an empty form -
        // honest, but the thing to fix once addresses have a store.
        onEdit={() => leaveAddressSheetFor(() => onAddAddress?.())}
      />

      <CouponSheet
        visible={couponOpen}
        itemTotal={itemTotal}
        appliedCode={couponCode}
        refusal={codeRefusal}
        onClose={() => setCouponOpen(false)}
        onApply={next => setCouponCode(next)}
        onRemove={() => setCouponCode(null)}
      />

      {/* The cross's own confirmation - there is no undo on an emptied
          basket, so it asks once, the same way removing a review photo does. */}
      <ConfirmSheet
        visible={clearing}
        icon="cart"
        tone="danger"
        title="Clear your cart?"
        body="Every service in your basket will be removed. This can't be undone."
        confirmLabel="Clear cart"
        cancelLabel="Keep my cart"
        onConfirm={() => {
          setClearing(false);
          onClearCart?.();
        }}
        onClose={() => setClearing(false)}
      />
    </View>
  );
}

// ---------------------------------------------------------------- small parts

/**
 * Back, the wordmark, and the address the whole basket is bound to.
 *
 * The address is in the header rather than only in its own section because it
 * decides what can be booked at all - a basket for the wrong city is not a
 * basket, and finding that out at the bottom of the page is too late.
 */
function Header({
  onBack,
  address,
  onPressAddress,
  inset,
  onClear,
}: {
  onBack: () => void;
  address: string;
  onPressAddress: () => void;
  /**
   * Extra side padding, so the strip lines up with the page's column on a
   * wide screen. Zero on a phone. The bar itself still spans the full width -
   * it is the app's chrome, and chrome that stops short of the edges reads as
   * a floating card.
   */
  inset: number;
  /**
   * The cross, in place of the old read-only cart glyph - see the note on
   * `Props.onClearCart`. Undefined hides it entirely: the empty-cart header
   * has nothing left to clear, and a cross that opens a confirm sheet for an
   * already-empty basket is a control with nothing to do.
   */
  onClear?: () => void;
}) {
  return (
    <View style={[styles.header, { paddingHorizontal: PAGE_INSET + inset }]}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Back"
        onPress={onBack}
        hitSlop={12}
        style={styles.headerBack}
      >
        <Icon name="arrowLeft" size={26} color={colors.ink} strokeWidth={2} />
      </Pressable>

      {/*
        The address takes the middle, where the title was.

        "Your Booking" said nothing the page did not: you arrived here by
        tapping a cart. The address is the one fact the whole basket depends
        on, so it gets the strip's centre rather than a caption under it.
      */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Service address, ${address}. Change`}
        // Not `onBack`. Tapping the address to change it and being returned to
        // Home is the one thing it must not do.
        onPress={onPressAddress}
        style={({ pressed }) => [
          styles.headerAddress,
          pressed ? styles.pressed : null,
        ]}
      >
        <Icon name="pin" size={17} color={colors.primary} />
        <Text
          variant="label"
          numberOfLines={2}
          style={styles.headerAddressText}
        >
          {address}
        </Text>
        <Icon name="chevronDown" size={15} color={colors.inkMuted} />
      </Pressable>

      {/*
        The cross, where a read-only cart glyph used to balance the arrow.

        The glyph made sense when the cart could be reached from several
        places at once and this was the one screen it always led to - it
        told you where you already were. Cart is its own tab now, so the bar
        underneath already says that; a plain mark here would be the one
        control on the page that has nothing to do. Clearing the basket is
        the thing left worth a control at this spot.
      */}
      {onClear ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Clear cart"
          onPress={onClear}
          hitSlop={10}
          style={({ pressed }) => [
            styles.headerClear,
            styles.headerClearPad,
            pressed ? styles.pressed : null,
          ]}
        >
          <Icon
            name="plus"
            size={18}
            color={colors.ink}
            strokeWidth={2.5}
            style={styles.headerClearGlyph}
          />
        </Pressable>
      ) : (
        <View style={styles.headerClear} />
      )}
    </View>
  );
}

/*
 * `PriceRow`, not `PriceLine`.
 *
 * `PriceLine` is now a type imported from `src/data/payment.ts`, and a local
 * function of the same name is a duplicate declaration - Babel refuses the
 * module outright, which is the red screen. The row component is renamed
 * because the type is the shared thing and this is local to the cart.
 */
function PriceRow({
  label,
  value,
  tone = 'plain',
}: {
  label: string;
  value: string;
  tone?: 'plain' | 'credit';
}) {
  const tint = tone === 'credit' ? colors.primary : colors.inkBody;
  return (
    <View style={styles.priceLine}>
      <Text variant="bodySm" color={tint}>
        {label}
      </Text>
      <Text variant="bodySm" color={tint}>
        {value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  /*
   * White, not the app's pale green page tint.
   *
   * The tint is what read as a band behind the total and the button: the nav
   * bar below them is a white pill, so the two surfaces met in a visible line
   * right where the checkout row sits. On one continuous white the row has no
   * ground of its own and the button is simply the next thing on the page.
   *
   * The sections inside the page are also white, and they do not rely on the
   * page tint to separate them: each `card` ends in an 8pt `surfaceMuted`
   * band, which is the divider that was already doing that work.
   */
  root: {
    flex: 1,
    backgroundColor: colors.surface,
  },

  // ------------------------------------------------------------- the header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: PAGE_INSET,
    // Room above the row. The safe-area inset stops at the status bar, which
    // left the arrow sitting directly under the clock.
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    backgroundColor: colors.surface,
    borderBottomWidth: hairline.borderWidth,
    borderBottomColor: hairline.borderColor,
  },
  /* The glyph's ink sits high in its box, so centring the box alone still left
     the arrow reading above the address beside it. */
  headerBack: {
    marginTop: 3,
  },
  /* Takes the middle and centres inside it, so the address sits on the strip's
     true centre however long it is, and wraps rather than truncating. */
  headerAddress: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  headerAddressText: {
    flexShrink: 1,
  },
  /* Sized to the arrow opposite it, so the address sits on the strip's middle
     - and to the same 26pt the home header uses, so the two agree. */
  /* Same 26pt footprint the old cart glyph held, so the address still
     centres between two equal-width ends whether or not the cross shows. */
  headerClear: {
    width: 26,
    height: 26,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /*
   * The pad is what makes the cross findable.
   *
   * Bare, it was a 2pt stroke in the same ink and the same weight as the back
   * arrow at the other end of the strip - two identical marks, one of which
   * empties the basket. The tinted disc gives it an edge of its own, so it
   * reads as a button sitting on the header rather than as a second glyph in
   * the chrome. Only the live control gets it; the empty-cart spacer stays
   * invisible, which is why this is a separate style and not folded into
   * `headerClear` itself.
   */
  headerClearPad: {
    borderRadius: 13,
    backgroundColor: colors.surfaceMuted,
  },
  /* The icon set has no cross; a rotated plus is the same shape. */
  headerClearGlyph: {
    transform: [{ rotate: '45deg' }],
  },

  // --------------------------------------------------------------- the page
  page: {
    paddingBottom: spacing.xxl,
  },
  /*
   * Full width, with the padding on the inside so the surface reaches both
   * edges and only the content is inset.
   *
   * Not cards. Eight rounded panels floating on a background turned one
   * checkout into eight things to consider separately, and the inset margins
   * cost width on every line inside them - the price rows and the booking
   * buttons both wanted it back. The separator is a thick band rather than a
   * hairline: at this length the page needs to read as distinct stretches.
   */
  card: {
    paddingHorizontal: PAGE_INSET,
    paddingVertical: spacing.lg,
    backgroundColor: colors.surface,
    borderBottomWidth: spacing.sm,
    borderBottomColor: colors.surfaceMuted,
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  cardHeadText: {
    flex: 1,
  },
  cardTitle: {
    marginBottom: spacing.md,
  },
  /* A whole card that is one tappable row. */
  rowCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  /*
   * The wallet switch, drawn rather than a platform `Switch`.
   *
   * A track with a knob that slides to the far side when on. No animation:
   * the row it lives on is itself the control, and the press feedback plus
   * the colour change already say it happened.
   */
  toggle: {
    width: 42,
    height: 24,
    borderRadius: 12,
    padding: 3,
    justifyContent: 'center',
    backgroundColor: colors.border,
  },
  toggleOn: {
    backgroundColor: colors.primary,
  },
  knob: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: colors.surface,
  },
  /* `alignSelf` rather than a left offset, so the knob sits against whichever
     end of the track it belongs to without the two numbers being kept in
     step by hand. */
  knobOn: {
    alignSelf: 'flex-end',
  },
  rowCardText: {
    flex: 1,
  },
  disclosure: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },

  // ------------------------------------------------------------- the basket
  // No top rule: the gap already separates one group from the next, and the
  // line read as the card being cut in half.
  group: {
    paddingTop: spacing.md,
  },
  /* Centred and short of both margins. Run to the edges it made the basket
     read as several cards stacked; left-aligned at half width it read as an
     underline for the group above it. */
  groupRule: {
    width: '84%',
    alignSelf: 'center',
    height: hairline.borderWidth,
    marginTop: spacing.md,
    backgroundColor: hairline.borderColor,
  },
  groupHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    // Tight to the lines under it: the heading and its services are one block,
    // and the old gap read as two.
    marginBottom: 0,
  },
  /* The weight lives in `title` now, so this carries nothing of its own. */
  groupTitle: {},
  pill: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceAccent,
  },
  line: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingLeft: GROUP_INDENT,
  },
  lineTime: {
    alignItems: 'flex-start',
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  lineBody: {
    flex: 1,
  },
  /* Stepper over price, so the number and what it costs read as one column
     rather than two things competing across the row. */
  lineRight: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  /* Under the name, inside the line's own column - deliberately NOT beside
     the quantity stepper on the right, which means a different thing. */
  hourRow: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  hourChip: {
    minWidth: 30,
    alignItems: 'center',
    paddingVertical: 2,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.pill,
    ...hairline,
    borderColor: colors.borderStrong,
    backgroundColor: colors.surface,
  },
  hourChipPicked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  step: {
    width: 18,
    alignItems: 'center',
  },
  /* A pill sized to its own words, sitting at the left under the last line -
     it follows the basket rather than framing it. Filled rather than outlined:
     against a white section an outline alone read as a disabled control. */
  more: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: spacing.xs,
    marginTop: spacing.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceAccent,
  },

  // -------------------------------------------------------- what goes with
  rail: {
    gap: spacing.sm,
  },
  /*
   * No `gap`, because the card is not four evenly spaced things.
   *
   * It is a picture, then a block: name, price and duration belong together
   * and are read as one. A uniform gap put the same air between the name and
   * its price as between the picture and the name, which left the price
   * floating free of the service it belongs to. The spacing is set on the
   * pieces below instead.
   */
  suggest: {
    width: SUGGEST_WIDTH,
  },
  suggestArt: {
    width: SUGGEST_WIDTH,
    height: 84,
    borderRadius: radii.sm,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  photo: {
    width: '100%',
    height: '100%',
  },
  suggestAdd: {
    position: 'absolute',
    right: spacing.xs,
    bottom: spacing.xs,
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  /*
   * `minHeight` is gone, and it is what the gap was.
   *
   * It reserved two lines so every card's price sat on the same baseline
   * however long its name ran. With only a price underneath that was worth
   * the empty line; now the name is followed by a price AND a duration, and
   * fifteen points of nothing under a one-line name separated the name from
   * the block it heads.
   *
   * The cost is that cards with a one-line name end shorter than their
   * neighbours. In a horizontal rail they align at the top, so that shows as
   * a slightly ragged lower edge - which reads as cards of different lengths,
   * where the old spacing read as a mistake.
   */
  suggestName: {
    marginTop: spacing.xs,
  },
  /*
   * The struck figure and the price on ONE line, the way `ServiceCard` sets
   * them - the pair has to read as one statement, and a column would put the
   * card's own gap between the two halves of it.
   */
  suggestPrices: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing.xs,
  },
  suggestWas: {
    textDecorationLine: 'line-through',
  },
  suggestDuration: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    marginTop: 2,
  },

  // ----------------------------------------------------------- the picker
  modes: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  mode: {
    flex: 1,
    alignItems: 'center',
    gap: 2,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xs,
    borderRadius: radii.sm,
    backgroundColor: colors.surface,
    ...hairline,
  },
  modeOn: {
    backgroundColor: colors.surfaceAccent,
    borderColor: colors.primary,
  },
  /* A paler fill, not `opacity: 0.5` over the whole tile - the ink is
     already `inkDisabled` above, and multiplying the two took the label
     under 2:1 while fading the tile's outline into the card behind it. */
  modeOff: {
    backgroundColor: colors.background,
    borderColor: colors.border,
  },
  modeNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
    padding: spacing.md,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceAccent,
  },
  modeNoteText: {
    flex: 1,
  },

  // ---------------------------------------------------------- the address
  addressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  addressMark: {
    width: 36,
    height: 36,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  addressText: {
    flex: 1,
    gap: 2,
  },

  // ----------------------------------------------------------- the numbers
  prices: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  priceLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.xs,
  },
  totalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  saved: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
    padding: spacing.sm,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceAccent,
  },

  // --------------------------------------------------------------- the tip
  tips: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  tip: {
    flex: 1,
    paddingVertical: spacing.sm,
    borderRadius: radii.sm,
    backgroundColor: colors.surface,
    ...hairline,
  },
  tipOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  /* The typed-amount row, under the chips rather than inside them: at a
     quarter of the row's width the field would fit about three digits. */
  customTip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radii.sm,
    backgroundColor: colors.surface,
    ...hairline,
  },
  customTipInput: {
    flex: 1,
    ...inputText,
    minHeight: 44,
  },

  policyBody: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },

  // ---------------------------------------------------------------- footer
  /*
   * The total and the button on the page, not on a panel of their own.
   *
   * The white ground and the hairline above it drew a bar across the foot of
   * the screen, so the button read as a control mounted on a strip rather
   * than as the page's own next step. The rule went with it: it was the edge
   * of that strip, and with no panel there is no edge to draw.
   */
  footer: {
    gap: spacing.sm,
    paddingHorizontal: PAGE_INSET,
    paddingTop: spacing.md,
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  /* Reads as one tappable line, not a box with a paragraph beside it: the
     whole row is the target, which is what a thumb expects. */
  termsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    paddingBottom: spacing.sm,
  },
  /* Shown only after somebody pressed the button without ticking - a border
     that is always there would read as an error on a form nobody filled in
     wrong yet. */
  termsRowNudged: {
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.danger,
    padding: spacing.sm,
    marginBottom: spacing.xs,
  },
  termsBox: {
    width: 20,
    height: 20,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  termsBoxOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  termsText: {
    flex: 1,
    lineHeight: 17,
  },
  footerTotal: {
    minWidth: 92,
  },
  footerAction: {
    flex: 1,
  },
  footerNote: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },

  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    paddingHorizontal: PAGE_INSET,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default CartScreen;
